import { IconCopy } from '@developerz/ui';
import { createSignal, type JSX } from 'solid-js';
import { t } from '../i18n';
import { copyText } from '../lib/clipboard';
import type { OrgInvitationMint } from '../lib/org-members';
import { absolute } from '../lib/time';
import { Button } from './ui/Button';

// The invitation an email invite just minted (arc 101 slice 01). Presenter —
// `<InviteForm/>` owns the call, the busy state and the dismissal.
//
// THE LINK IS SHOWN because the mail is best-effort: `buildInvitationDeps` records a
// send failure and answers `mailed: false`, since the row is already committed
// (`apps/api/src/deps/invitations.ts`), and a mint whose mail did not leave says
// so instead of "sent" (deep sweep 03-13). If the mail never lands, this is
// the only copy of the accept URL that will ever exist — the row keeps a sha256
// and `listInvitations` structurally cannot project either column. Hence the
// `<MintedTokenNotice/>` stance: copy first, dismissal explicit, never a timed
// toast that could expire while its owner is switching windows.
//
// RESEND IS THE INVITE CALL, not a lane beside it: `createInvitation` re-arms the
// one live row for an address with a fresh token and a fresh expiry
// (`packages/db/src/queries/invitations.ts:154-162`). Which is exactly why a
// re-armed invitation SAYS the previous link stopped working — by then it may
// already be in a Slack thread.

/** Whether the last copy attempt landed — `failed` is a real answer, not silence. */
type CopyState = 'idle' | 'copied' | 'failed';

/**
 * The copy button's label per state. `satisfies Record<CopyState, …>` and not an
 * `invite_link_${state}` template: an interpolated catalog key is a lookup no
 * grep finds and no compiler checks, which is how `⟦key⟧` reaches a screen.
 */
const COPY_LABEL = {
  idle: 'org_members.invite_link_copy',
  copied: 'org_members.invite_link_copied',
  failed: 'org_members.invite_link_copy_failed',
} as const satisfies Record<CopyState, string>;

/**
 * Title + body for the three things a mint can be: fresh, re-armed, or minted with
 * a mail that did not go out (03-13). Spelled out for the same reason
 * {@link COPY_LABEL} is: the difference between them is a sentence a person acts
 * on — a re-armed invitation killed the link they may already have shared, and an
 * unmailed one reaches nobody unless they send the link themselves.
 */
const MINT_COPY = {
  fresh: { title: 'org_members.invite_sent_title', desc: 'org_members.invite_sent_desc' },
  reissued: {
    title: 'org_members.invite_reissued_title',
    desc: 'org_members.invite_reissued_desc',
  },
  unmailed: {
    title: 'org_members.invite_unmailed_title',
    desc: 'org_members.invite_unmailed_desc',
  },
} as const;

export function InvitationMintNotice(props: {
  readonly mint: OrgInvitationMint;
  readonly busy: boolean;
  readonly onResend: () => void;
  readonly onDismiss: () => void;
}): JSX.Element {
  const [copied, setCopied] = createSignal<CopyState>('idle');
  // `copyText` answers whether the write LANDED — a non-secure context has no
  // clipboard, and silence there reads as "the click did nothing".
  const copy = (): void => {
    void copyText(props.mint.accept_url).then((ok) => setCopied(ok ? 'copied' : 'failed'));
  };
  const text = (): (typeof MINT_COPY)[keyof typeof MINT_COPY] =>
    !props.mint.mailed
      ? MINT_COPY.unmailed
      : props.mint.reissued
        ? MINT_COPY.reissued
        : MINT_COPY.fresh;

  return (
    <div class="alert token-secret" role="status">
      <p class="alert__title">{t(text().title, { email: props.mint.email })}</p>
      <p class="alert__desc">{t(text().desc, { expires: absolute(props.mint.expires_at_iso) })}</p>
      <code class="code-block token-secret__value">{props.mint.accept_url}</code>
      <div class="token-secret__actions">
        <Button variant="outline" size="sm" onClick={copy}>
          <IconCopy size="0.875rem" />
          {t(COPY_LABEL[copied()])}
        </Button>
        <Button variant="outline" size="sm" disabled={props.busy} onClick={props.onResend}>
          {t('org_members.invite_resend')}
        </Button>
        <Button variant="ghost" size="sm" onClick={props.onDismiss}>
          {t('org_members.dismiss')}
        </Button>
      </div>
    </div>
  );
}
